<template>
  <Teleport to="body">
    <transition name="arca-modal-fade">
      <div
        v-if="abierto"
        class="arca-emit-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="emitirArcaTitle"
        @click.self="cerrar"
        @keydown.esc="cerrar"
      >
        <div class="arca-emit-sheet" :class="{ 'arca-emit-sheet--ok': !!resultado }">
          <header class="arca-emit-head">
            <div class="arca-emit-grab" aria-hidden="true" />
            <div class="arca-emit-head__row">
              <div class="min-w-0">
                <p class="arca-emit-eyebrow mb-0">Facturación ARCA</p>
                <h2 id="emitirArcaTitle" class="arca-emit-title mb-0">
                  {{ resultado ? 'Comprobante autorizado' : 'Emitir comprobante' }}
                </h2>
              </div>
              <button
                type="button"
                class="arca-emit-close"
                aria-label="Cerrar"
                :disabled="guardando"
                @click="cerrar"
              >
                <i class="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
            <div v-if="emisor.razon_social && !resultado" class="arca-emit-emisor">
              <div class="arca-emit-emisor__icon" aria-hidden="true">
                <i class="bi bi-building" />
              </div>
              <div class="min-w-0">
                <div class="arca-emit-emisor__name text-truncate">{{ emisor.razon_social }}</div>
                <div class="arca-emit-emisor__meta text-truncate">
                  <span v-if="emisor.cuit">CUIT {{ formatCuit(emisor.cuit) }}</span>
                  <span v-if="emisor.condicion_iva" class="arca-emit-dot">·</span>
                  <span v-if="emisor.condicion_iva">{{ labelCondicionEmisor }}</span>
                </div>
              </div>
            </div>
          </header>

          <form class="arca-emit-form" @submit.prevent="emitir">
            <div class="arca-emit-body">
              <div v-if="cargandoCatalogo" class="arca-emit-loading">
                <div class="spinner-border text-primary" role="status" aria-hidden="true" />
                <span>Cargando datos fiscales…</span>
              </div>

              <template v-else-if="resultado">
                <div class="arca-emit-success">
                  <div class="arca-emit-success__badge" aria-hidden="true">
                    <i class="bi bi-check2" />
                  </div>
                  <p class="arca-emit-success__tipo mb-1">{{ resultado.cbte_tipo_label }}</p>
                  <p class="arca-emit-success__nro mb-3">{{ resultado.numero_completo }}</p>
                  <dl class="arca-emit-success__grid">
                    <div>
                      <dt>CAE</dt>
                      <dd>{{ resultado.cae }}</dd>
                    </div>
                    <div v-if="resultado.cae_vto">
                      <dt>Vence</dt>
                      <dd>{{ resultado.cae_vto }}</dd>
                    </div>
                    <div>
                      <dt>Total</dt>
                      <dd>${{ money(resultado.imp_total) }}</dd>
                    </div>
                    <div v-if="resultado.imp_iva != null">
                      <dt>IVA</dt>
                      <dd>${{ money(resultado.imp_iva) }}</dd>
                    </div>
                  </dl>
                </div>
              </template>

              <template v-else>
                <div v-if="error" class="arca-emit-alert" role="alert">
                  <i class="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                  <span>{{ error }}</span>
                </div>

                <!-- Importe hero -->
                <section class="arca-emit-section arca-emit-section--amount">
                  <label class="arca-emit-label" for="arcaTotal">Importe total</label>
                  <div class="arca-emit-amount">
                    <span class="arca-emit-amount__currency" aria-hidden="true">$</span>
                    <input
                      id="arcaTotal"
                      v-model.number="form.imp_total"
                      class="arca-emit-amount__input"
                      type="number"
                      min="0.01"
                      step="0.01"
                      inputmode="decimal"
                      placeholder="0,00"
                      required
                    />
                  </div>
                  <p v-if="previewImportes" class="arca-emit-amount__hint mb-0">
                    Neto ${{ money(previewImportes.neto) }}
                    <template v-if="previewImportes.iva > 0">
                      · IVA {{ form.iva_alicuota }}% ${{ money(previewImportes.iva) }}
                    </template>
                    <template v-else> · sin IVA discriminado</template>
                  </p>
                </section>

                <!-- Tipo de comprobante -->
                <section class="arca-emit-section">
                  <div class="arca-emit-section__head">
                    <h3 class="arca-emit-section__title">Comprobante</h3>
                  </div>
                  <div class="arca-emit-tipos" role="listbox" aria-label="Tipo de comprobante">
                    <button
                      v-for="t in tipos"
                      :key="t.codigo"
                      type="button"
                      role="option"
                      class="arca-emit-tipo"
                      :class="{ 'arca-emit-tipo--on': Number(form.cbte_tipo) === t.codigo }"
                      :aria-selected="Number(form.cbte_tipo) === t.codigo"
                      @click="form.cbte_tipo = t.codigo"
                    >
                      <span class="arca-emit-tipo__letra">{{ t.letra || '?' }}</span>
                      <span class="arca-emit-tipo__label">{{ shortTipoLabel(t) }}</span>
                    </button>
                  </div>
                  <div class="arca-emit-fields">
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaPv">Punto de venta</label>
                      <select
                        id="arcaPv"
                        v-model.number="form.punto_venta"
                        class="form-select arca-emit-control"
                        required
                      >
                        <option :value="null" disabled>
                          {{ puntosVenta.length ? 'Elegí' : 'Sin PV en ARCA' }}
                        </option>
                        <option v-for="pv in puntosVenta" :key="pv.nro" :value="pv.nro">
                          PV {{ String(pv.nro).padStart(5, '0') }}
                        </option>
                      </select>
                    </div>
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaConcepto">Concepto</label>
                      <select id="arcaConcepto" v-model.number="form.concepto" class="form-select arca-emit-control">
                        <option :value="1">Productos</option>
                        <option :value="2">Servicios</option>
                        <option :value="3">Prod. y servicios</option>
                      </select>
                    </div>
                    <div v-if="requiereIva" class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaIva">Alícuota IVA</label>
                      <select id="arcaIva" v-model.number="form.iva_alicuota" class="form-select arca-emit-control">
                        <option v-for="p in ivaAlicuotas" :key="p" :value="p">{{ p }}%</option>
                      </select>
                    </div>
                  </div>
                </section>

                <!-- Receptor -->
                <section class="arca-emit-section">
                  <div class="arca-emit-section__head">
                    <h3 class="arca-emit-section__title">Receptor</h3>
                  </div>
                  <div class="arca-emit-fields">
                    <div class="arca-emit-field arca-emit-field--full">
                      <label class="arca-emit-label" for="arcaNombre">Nombre</label>
                      <input
                        id="arcaNombre"
                        v-model="form.receptor_nombre"
                        class="form-control arca-emit-control"
                        type="text"
                        maxlength="191"
                        placeholder="Opcional"
                        autocomplete="name"
                      />
                    </div>
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaDocTipo">Documento</label>
                      <select id="arcaDocTipo" v-model.number="form.doc_tipo" class="form-select arca-emit-control">
                        <option v-for="d in docTipos" :key="d.codigo" :value="d.codigo">{{ d.label }}</option>
                      </select>
                    </div>
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaDocNro">Número</label>
                      <input
                        id="arcaDocNro"
                        v-model="form.doc_nro"
                        class="form-control arca-emit-control"
                        type="text"
                        inputmode="numeric"
                        :disabled="form.doc_tipo === 99"
                        :placeholder="form.doc_tipo === 99 ? 'Consumidor final' : 'Sin guiones'"
                      />
                    </div>
                    <div class="arca-emit-field arca-emit-field--full">
                      <label class="arca-emit-label" for="arcaCondRec">Condición frente al IVA</label>
                      <select
                        id="arcaCondRec"
                        v-model.number="form.condicion_iva_receptor"
                        class="form-select arca-emit-control"
                      >
                        <option v-for="c in condiciones" :key="c.codigo" :value="c.codigo">{{ c.label }}</option>
                      </select>
                    </div>
                  </div>
                </section>

                <!-- Asociado NC/ND -->
                <section v-if="requiereAsoc" class="arca-emit-section arca-emit-section--asoc">
                  <div class="arca-emit-section__head">
                    <h3 class="arca-emit-section__title">Comprobante asociado</h3>
                    <p class="arca-emit-section__hint mb-0">Obligatorio para notas de crédito o débito</p>
                  </div>
                  <div class="arca-emit-fields">
                    <div class="arca-emit-field arca-emit-field--full">
                      <label class="arca-emit-label" for="arcaAsocTipo">Tipo</label>
                      <select
                        id="arcaAsocTipo"
                        v-model.number="form.asociado_tipo"
                        class="form-select arca-emit-control"
                        required
                      >
                        <option :value="null" disabled>Elegí la factura original</option>
                        <option v-for="t in tiposFactura" :key="'a' + t.codigo" :value="t.codigo">
                          {{ t.label }}
                        </option>
                      </select>
                    </div>
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaAsocPv">Punto de venta</label>
                      <select
                        id="arcaAsocPv"
                        v-model.number="form.asociado_pto_vta"
                        class="form-select arca-emit-control"
                        required
                      >
                        <option :value="null" disabled>Elegí</option>
                        <option v-for="pv in puntosVenta" :key="'asoc' + pv.nro" :value="pv.nro">
                          PV {{ String(pv.nro).padStart(5, '0') }}
                        </option>
                      </select>
                    </div>
                    <div class="arca-emit-field">
                      <label class="arca-emit-label" for="arcaAsocNro">Número</label>
                      <input
                        id="arcaAsocNro"
                        v-model.number="form.asociado_nro"
                        class="form-control arca-emit-control"
                        type="number"
                        min="1"
                        required
                      />
                    </div>
                  </div>
                </section>
              </template>
            </div>

            <footer class="arca-emit-foot">
              <button
                type="button"
                class="btn arca-emit-btn arca-emit-btn--ghost"
                :disabled="guardando"
                @click="cerrar"
              >
                {{ resultado ? 'Cerrar' : 'Cancelar' }}
              </button>
              <button
                v-if="!resultado"
                type="submit"
                class="btn arca-emit-btn arca-emit-btn--primary"
                :disabled="guardando || cargandoCatalogo"
              >
                <span
                  v-if="guardando"
                  class="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                />
                <i v-else class="bi bi-shield-check me-2" aria-hidden="true" />
                {{ guardando ? 'Autorizando…' : 'Autorizar en ARCA' }}
              </button>
              <button
                v-else
                type="button"
                class="btn arca-emit-btn arca-emit-btn--primary"
                @click="emitirOtro"
              >
                Emitir otro
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import axios from 'axios';

const FORM_BASE = () => ({
  cbte_tipo: 11,
  punto_venta: null,
  imp_total: null,
  doc_tipo: 99,
  doc_nro: '',
  condicion_iva_receptor: 5,
  receptor_nombre: '',
  iva_alicuota: 21,
  concepto: 1,
  asociado_tipo: null,
  asociado_pto_vta: null,
  asociado_nro: null,
  pedido_id: null,
});

const COND_EMISOR_LABEL = {
  monotributo: 'Monotributo',
  responsable_inscripto: 'Resp. inscripto',
  exento: 'Exento',
  no_responsable: 'No responsable',
  consumidor_final: 'Consumidor final',
};

export default {
  name: 'EmitirComprobanteArcaModal',
  props: {
    modelValue: { type: Boolean, default: false },
    preset: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'emitido'],
  data() {
    return {
      cargandoCatalogo: false,
      guardando: false,
      error: '',
      resultado: null,
      emisor: {},
      puntosVenta: [],
      tipos: [],
      docTipos: [],
      condiciones: [],
      ivaAlicuotas: [21, 10.5, 27],
      form: FORM_BASE(),
    };
  },
  computed: {
    abierto: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    },
    tipoActual() {
      return this.tipos.find((t) => t.codigo === Number(this.form.cbte_tipo)) || null;
    },
    requiereIva() {
      return !!(this.tipoActual && this.tipoActual.requiereIva);
    },
    requiereAsoc() {
      return !!(this.tipoActual && this.tipoActual.requiereAsoc);
    },
    tiposFactura() {
      return this.tipos.filter((t) => t.clase === 'factura');
    },
    labelCondicionEmisor() {
      return COND_EMISOR_LABEL[this.emisor.condicion_iva] || this.emisor.condicion_iva || '';
    },
    previewImportes() {
      const total = Number(this.form.imp_total);
      if (!(total > 0)) return null;
      if (!this.requiereIva) {
        return { neto: total, iva: 0 };
      }
      const pct = Number(this.form.iva_alicuota) || 21;
      const neto = Math.round((total / (1 + pct / 100) + Number.EPSILON) * 100) / 100;
      const iva = Math.round((total - neto + Number.EPSILON) * 100) / 100;
      return { neto, iva };
    },
  },
  watch: {
    modelValue(v) {
      if (v) {
        document.body.classList.add('arca-emit-open');
        void this.abrir();
      } else {
        document.body.classList.remove('arca-emit-open');
      }
    },
    'form.doc_tipo'(v) {
      if (Number(v) === 99) this.form.doc_nro = '';
    },
  },
  beforeUnmount() {
    document.body.classList.remove('arca-emit-open');
  },
  methods: {
    money(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return '—';
      return n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCuit(c) {
      const s = String(c || '').replace(/\D/g, '');
      if (s.length !== 11) return s;
      return `${s.slice(0, 2)}-${s.slice(2, 10)}-${s.slice(10)}`;
    },
    shortTipoLabel(t) {
      const raw = String(t.label || '');
      return raw
        .replace(/^Factura\s+/i, 'Fact. ')
        .replace(/^Nota de Crédito\s+/i, 'NC ')
        .replace(/^Nota de Débito\s+/i, 'ND ');
    },
    async abrir() {
      this.error = '';
      this.resultado = null;
      this.form = FORM_BASE();
      if (this.preset) {
        if (this.preset.imp_total != null) this.form.imp_total = Number(this.preset.imp_total);
        if (this.preset.receptor_nombre) this.form.receptor_nombre = this.preset.receptor_nombre;
        if (this.preset.pedido_id) this.form.pedido_id = this.preset.pedido_id;
      }
      this.cargandoCatalogo = true;
      try {
        const { data } = await axios.get('/arca/catalogo');
        this.emisor = data.emisor || {};
        this.puntosVenta = Array.isArray(data.puntos_venta) ? data.puntos_venta : [];
        this.tipos = data.tipos && data.tipos.length ? data.tipos : data.tipos_todos || [];
        this.docTipos = data.doc_tipos || [];
        this.condiciones = data.condiciones_iva_receptor || [];
        this.ivaAlicuotas = data.iva_alicuotas || [21, 10.5, 27];
        const defs = data.defaults || {};
        if (defs.cbte_tipo) this.form.cbte_tipo = Number(defs.cbte_tipo);
        else if (this.tipos.length) this.form.cbte_tipo = this.tipos[0].codigo;
        if (defs.concepto != null) this.form.concepto = Number(defs.concepto);
        if (defs.doc_tipo != null) this.form.doc_tipo = Number(defs.doc_tipo);
        if (defs.condicion_iva_receptor != null) {
          this.form.condicion_iva_receptor = Number(defs.condicion_iva_receptor);
        }
        if (defs.iva_alicuota != null) this.form.iva_alicuota = Number(defs.iva_alicuota);
        if (this.emisor.punto_venta) {
          this.form.punto_venta = Number(this.emisor.punto_venta);
        } else if (this.puntosVenta.length === 1) {
          this.form.punto_venta = this.puntosVenta[0].nro;
        }
        if (this.preset && this.preset.cbte_tipo) this.form.cbte_tipo = Number(this.preset.cbte_tipo);
        this.aplicarPresetCompleto(this.preset);
        if (!this.puntosVenta.length) {
          this.error =
            'No hay puntos de venta cargados. Andá a Perfil → Cobros, vinculá ARCA y elegí el punto de venta.';
        }
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          'No se pudo cargar el catálogo. ¿El certificado está vinculado?';
      } finally {
        this.cargandoCatalogo = false;
      }
    },
    aplicarPresetCompleto(preset) {
      const p = preset;
      if (!p) return;
      if (p.imp_total != null) this.form.imp_total = Number(p.imp_total);
      if (p.receptor_nombre) this.form.receptor_nombre = p.receptor_nombre;
      if (p.pedido_id) this.form.pedido_id = p.pedido_id;
      if (p.cbte_tipo) this.form.cbte_tipo = Number(p.cbte_tipo);
      if (p.punto_venta != null) this.form.punto_venta = Number(p.punto_venta);
      if (p.doc_tipo != null) this.form.doc_tipo = Number(p.doc_tipo);
      if (p.doc_nro != null && p.doc_nro !== '') this.form.doc_nro = String(p.doc_nro);
      if (p.condicion_iva_receptor != null) this.form.condicion_iva_receptor = Number(p.condicion_iva_receptor);
      if (p.iva_alicuota != null) this.form.iva_alicuota = Number(p.iva_alicuota);
      if (p.asociado_tipo != null) this.form.asociado_tipo = Number(p.asociado_tipo);
      if (p.asociado_pto_vta != null) this.form.asociado_pto_vta = Number(p.asociado_pto_vta);
      if (p.asociado_nro != null) this.form.asociado_nro = Number(p.asociado_nro);
    },
    emitirOtro() {
      this.resultado = null;
      this.error = '';
      const pedidoId = this.form.pedido_id;
      const pv = this.form.punto_venta;
      this.form = FORM_BASE();
      this.form.punto_venta = pv || this.emisor.punto_venta || (this.puntosVenta[0] && this.puntosVenta[0].nro) || null;
      this.form.pedido_id = pedidoId;
      if (this.tipos.length) this.form.cbte_tipo = this.tipos[0].codigo;
    },
    cerrar() {
      if (this.guardando) return;
      this.abierto = false;
    },
    async emitir() {
      this.error = '';
      if (!(Number(this.form.imp_total) > 0)) {
        this.error = 'Indicá un importe total mayor a 0.';
        return;
      }
      if (!this.form.punto_venta) {
        this.error = 'Elegí el punto de venta.';
        return;
      }
      this.guardando = true;
      try {
        const payload = {
          cbte_tipo: this.form.cbte_tipo,
          punto_venta: this.form.punto_venta,
          imp_total: this.form.imp_total,
          doc_tipo: this.form.doc_tipo,
          doc_nro: this.form.doc_tipo === 99 ? 0 : this.form.doc_nro,
          condicion_iva_receptor: this.form.condicion_iva_receptor,
          receptor_nombre: this.form.receptor_nombre,
          iva_alicuota: this.form.iva_alicuota,
          concepto: this.form.concepto,
          pedido_id: this.form.pedido_id || undefined,
        };
        if (this.requiereAsoc) {
          payload.asociado_tipo = this.form.asociado_tipo;
          payload.asociado_pto_vta = this.form.asociado_pto_vta;
          payload.asociado_nro = this.form.asociado_nro;
        }
        const { data } = await axios.post('/arca/comprobantes/emitir', payload);
        this.resultado = data;
        this.$emit('emitido', data);
      } catch (e) {
        this.error =
          (e.response && e.response.data && e.response.data.message) ||
          'No se pudo emitir el comprobante.';
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style scoped>
.arca-emit-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  background: rgba(15, 23, 42, 0.52);
}

.arca-emit-sheet {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: min(94dvh, 920px);
  background: var(--po-surface, #fff);
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 40px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.arca-emit-grab {
  width: 40px;
  height: 4px;
  margin: 8px auto 4px;
  border-radius: 999px;
  background: #cbd5e1;
}

.arca-emit-head {
  flex-shrink: 0;
  padding: 0 1.1rem 0.85rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.arca-emit-head__row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.arca-emit-eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--po-muted, #64748b);
}

.arca-emit-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--po-text, #0f172a);
  line-height: 1.25;
}

.arca-emit-close {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.arca-emit-close:disabled {
  opacity: 0.5;
}

.arca-emit-emisor {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-top: 0.85rem;
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: #f1f5f9;
}

.arca-emit-emisor__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #fff;
  color: var(--po-primary, #2563eb);
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.arca-emit-emisor__name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--po-text, #0f172a);
}

.arca-emit-emisor__meta {
  font-size: 0.75rem;
  color: var(--po-muted, #64748b);
}

.arca-emit-dot {
  margin: 0 0.25rem;
}

.arca-emit-form {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.arca-emit-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 1rem 1.1rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
}

.arca-emit-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 160px;
  color: var(--po-muted, #64748b);
  font-size: 0.9rem;
}

.arca-emit-alert {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 0.86rem;
  line-height: 1.35;
}

.arca-emit-alert i {
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.arca-emit-section {
  padding: 0.85rem 0.9rem;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}

.arca-emit-section--amount {
  background: linear-gradient(145deg, #eff6ff 0%, #f0fdfa 100%);
  border-color: #dbeafe;
}

.arca-emit-section--asoc {
  background: #fff7ed;
  border-color: #ffedd5;
}

.arca-emit-section__head {
  margin-bottom: 0.7rem;
}

.arca-emit-section__title {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #475569;
}

.arca-emit-section__hint {
  margin-top: 0.2rem;
  font-size: 0.78rem;
  color: #9a3412;
}

.arca-emit-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #475569;
}

.arca-emit-amount {
  display: flex;
  align-items: baseline;
  gap: 0.15rem;
}

.arca-emit-amount__currency {
  font-size: 1.55rem;
  font-weight: 700;
  color: #1e3a8a;
  line-height: 1;
}

.arca-emit-amount__input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  font-size: 2rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--po-text, #0f172a);
  outline: none;
  padding: 0;
  /* hide number spinners */
  -moz-appearance: textfield;
}

.arca-emit-amount__input::-webkit-outer-spin-button,
.arca-emit-amount__input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.arca-emit-amount__hint {
  margin-top: 0.45rem;
  font-size: 0.78rem;
  color: #334155;
}

.arca-emit-tipos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.45rem;
  margin-bottom: 0.75rem;
}

.arca-emit-tipo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  min-height: 52px;
  padding: 0.45rem 0.55rem;
  border-radius: 12px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.arca-emit-tipo--on {
  border-color: var(--po-primary, #2563eb);
  background: var(--po-primary-soft, #dbeafe);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.2);
}

.arca-emit-tipo__letra {
  font-size: 0.95rem;
  font-weight: 800;
  color: var(--po-primary, #2563eb);
  line-height: 1;
}

.arca-emit-tipo__label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #334155;
  line-height: 1.2;
}

.arca-emit-fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.65rem 0.7rem;
}

.arca-emit-field--full {
  grid-column: 1 / -1;
}

.arca-emit-control {
  min-height: 42px;
  border-radius: 10px;
  border-color: #dbe3ee;
  font-size: 0.92rem;
}

.arca-emit-control:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}

.arca-emit-success {
  text-align: center;
  padding: 1.25rem 0.5rem 0.75rem;
}

.arca-emit-success__badge {
  width: 56px;
  height: 56px;
  margin: 0 auto 0.85rem;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  display: grid;
  place-items: center;
  font-size: 1.6rem;
}

.arca-emit-success__tipo {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
}

.arca-emit-success__nro {
  font-size: 1.45rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: var(--po-text, #0f172a);
  font-variant-numeric: tabular-nums;
}

.arca-emit-success__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  margin: 0;
  text-align: left;
}

.arca-emit-success__grid > div {
  padding: 0.65rem 0.75rem;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
}

.arca-emit-success__grid dt {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.15rem;
}

.arca-emit-success__grid dd {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  word-break: break-all;
}

.arca-emit-foot {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 0.55rem;
  padding: 0.85rem 1.1rem calc(0.85rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #e2e8f0;
  background: #fff;
}

.arca-emit-btn {
  min-height: 46px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.92rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.arca-emit-btn--ghost {
  background: #f1f5f9;
  border: 0;
  color: #334155;
}

.arca-emit-btn--primary {
  background: var(--po-primary, #2563eb);
  border: 0;
  color: #fff;
}

.arca-emit-btn--primary:disabled {
  opacity: 0.65;
}

/* Desktop */
@media (min-width: 768px) {
  .arca-emit-overlay {
    align-items: center;
    padding: 1.5rem;
  }

  .arca-emit-sheet {
    width: min(560px, 100%);
    max-height: min(90vh, 820px);
    border-radius: 18px;
    box-shadow: var(--po-shadow-hover, 0 8px 28px rgba(15, 23, 42, 0.12));
  }

  .arca-emit-grab {
    display: none;
  }

  .arca-emit-head {
    padding: 1.15rem 1.35rem 1rem;
  }

  .arca-emit-title {
    font-size: 1.3rem;
  }

  .arca-emit-body {
    padding: 1.15rem 1.35rem 0.65rem;
    gap: 1rem;
  }

  .arca-emit-tipos {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.55rem;
  }

  .arca-emit-tipo {
    min-height: 58px;
    padding: 0.55rem 0.65rem;
  }

  .arca-emit-tipo__label {
    font-size: 0.78rem;
  }

  .arca-emit-amount__input {
    font-size: 2.25rem;
  }

  .arca-emit-foot {
    grid-template-columns: auto 1fr;
    padding: 1rem 1.35rem 1.15rem;
  }

  .arca-emit-btn--ghost {
    min-width: 120px;
  }
}

@media (max-width: 380px) {
  .arca-emit-tipos {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* transitions */
.arca-modal-fade-enter-active,
.arca-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.arca-modal-fade-enter-active .arca-emit-sheet,
.arca-modal-fade-leave-active .arca-emit-sheet {
  transition: transform 0.22s ease;
}

.arca-modal-fade-enter-from,
.arca-modal-fade-leave-to {
  opacity: 0;
}

.arca-modal-fade-enter-from .arca-emit-sheet,
.arca-modal-fade-leave-to .arca-emit-sheet {
  transform: translateY(18px);
}

@media (min-width: 768px) {
  .arca-modal-fade-enter-from .arca-emit-sheet,
  .arca-modal-fade-leave-to .arca-emit-sheet {
    transform: translateY(10px) scale(0.98);
  }
}
</style>

<style>
/* lock scroll while open */
body.arca-emit-open {
  overflow: hidden;
}
</style>
